// UAE Pass endpoint functions. Async, call `authApi`, throw `AppError` (mapped
// by the interceptor), and return DTOs — parsers run in the mutation hooks.
// Each response is unwrapped from the auth-service `{ success, message, data }`
// envelope here so the rest of the app never sees the envelope.
//
// No UAE Pass secrets live on the client: client id, redirect URI, scope, acr,
// and the anti-CSRF state are all owned by the auth service. The frontend only
// asks for an authorize URL, redirects the browser to it, then posts back the
// returned code + state.

import { authApi } from '@core/api/httpClient'
import { authEndpointPaths } from './authEndpointPaths'
import type {
  ApiEnvelope,
  UaePassAuthorizeUrlDto,
  UaePassCallbackDto,
  UaePassLogoutUrlDto,
  UaePassRegisterDto,
  LogoutResponseDto,
} from './authContracts'

export async function getUaePassAuthorizeUrl(portalCode: string): Promise<UaePassAuthorizeUrlDto> {
  const { data } = await authApi.get<ApiEnvelope<UaePassAuthorizeUrlDto>>(
    authEndpointPaths.UAE_PASS_AUTHORIZE_URL,
    { params: { portalCode } },
  )
  return data.data
}

// Front-channel UAE Pass single-logout URL for a login that never produced a
// session. Public on the auth service (there is no token to send) because a
// failed callback still leaves the UAE Pass SSO cookie in place — without
// clearing it, the next sign-in re-approves the same identity silently and the
// user is stuck on the same error.
export async function getUaePassLogoutUrl(portalCode: string): Promise<UaePassLogoutUrlDto> {
  const { data } = await authApi.get<ApiEnvelope<UaePassLogoutUrlDto>>(
    authEndpointPaths.UAE_PASS_LOGOUT_URL,
    { params: { portalCode } },
  )
  return data.data
}

export async function exchangeUaePassCode(payload: {
  code: string
  state: string
}): Promise<UaePassCallbackDto> {
  const { data } = await authApi.post<ApiEnvelope<UaePassCallbackDto>>(
    authEndpointPaths.UAE_PASS_CALLBACK,
    payload,
  )
  return data.data
}

export async function registerWithUaePass(payload: {
  signupTicket: string
  mobileCountryCode: string
  mobile: string
}): Promise<UaePassRegisterDto> {
  const { data } = await authApi.post<ApiEnvelope<UaePassRegisterDto>>(
    authEndpointPaths.UAE_PASS_REGISTER,
    payload,
  )
  return data.data
}

// Invalidates the server session. Must run while the access token is still
// present (authApi injects the Bearer), so the caller logs out server-side
// BEFORE clearing local tokens. Returns the UAE Pass single-logout URL when the
// session was a UAE Pass login.
export async function logout(payload: { refreshToken: string }): Promise<LogoutResponseDto> {
  const { data } = await authApi.post<ApiEnvelope<LogoutResponseDto>>(
    authEndpointPaths.LOGOUT,
    payload,
  )
  return data.data
}
